import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';
const STORAGE_KEY = 'kaf-warehouse-theme';

function readSaved(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' ? 'light' : 'dark';   // default = dark
}

function applyTheme(theme: Theme) {
  const html = document.documentElement;
  html.classList.toggle('theme-light', theme === 'light');
  html.dataset.theme = theme;  // available to selectors that prefer attribute syntax
}

export function useTheme(): { theme: Theme; toggle: () => void; set: (t: Theme) => void } {
  const [theme, setTheme] = useState<Theme>(readSaved);

  // Keep <html> class in sync whenever state changes (covers initial mount too)
  useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === 'light' ? 'dark' : 'light')), []);
  return { theme, toggle, set: setTheme };
}

/** Run before React mounts to avoid a flash of wrong theme. Called in main.tsx. */
export function applyInitialTheme(): void {
  applyTheme(readSaved());
}
